<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Finance Inventory</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Calendar:</label>
            <input
              v-model="currentDate"
              type="date"
              @keydown.prevent
              @change="loadInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Select Department/Category:</label>
            <select
              v-model="selectedScopeKey"
              @change="loadInventory"
              :disabled="loadingCategories"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
            >
              <option value="" disabled>
                {{ loadingCategories ? 'Loading...' : 'Select Department/Category' }}
              </option>
              <option
                v-for="opt in departmentCategoryOptions"
                :key="opt.key"
                :value="opt.key"
              >
                {{ opt.name }}
              </option>
            </select>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <table class="w-full daily-inventory-table">
          <thead>
            <tr>
              <th class="w-10 text-center sticky-col bg-[#f1f5f9] no-side-border">No.</th>
              <th class="min-w-[35px] sticky-col left-[40px] bg-[#f1f5f9] no-side-border">Ingredient Name</th>
              <th class="w-24">U.O.M st</th>
              <th class="w-28">Qty Pack</th>
              <th class="w-36">Volume</th>
              <th class="w-32">Converted</th>
              <th class="w-28">Qty Single</th>
              <th class="w-32">Total Qty</th>
              <th class="w-28 text-right px-4">Unit Price</th>
              <th class="w-28 text-right px-4">T.Price</th>
              <th class="w-32">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in inventory"
              :key="index"
              class="hover:bg-slate-50 transition-colors group"
            >
              <td class="text-center sticky-col group-hover:bg-slate-50 no-side-border bg-white">
                {{ index + 1 }}
              </td>
              <td class="sticky-col left-[40px] group-hover:bg-slate-50 no-side-border font-medium bg-white">
                {{ row.ingredientName || row.name }}
              </td>
              <td>{{ row.uomStore || row.uomFB }}</td>
              <td class="w-28">
                <input
                  v-model.number="row.qtyPack"
                  type="number"
                  @input="calcTotal(index)"
                  :disabled="inventoryFieldDisabled(row, 'qtyPack')"
                  :class="inventoryFieldDisabled(row, 'qtyPack') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-16 px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm"
                >
              </td>
              <td class="w-36">
                <input
                  v-model.number="row.volume"
                  type="number"
                  @input="calcTotal(index)"
                  :disabled="inventoryFieldDisabled(row, 'volume')"
                  :class="inventoryFieldDisabled(row, 'volume') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-16 px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm"
                >
              </td>
              <td>{{ formatQuantity(row.converted) }}</td>
              <td class="w-28">
                <input
                  v-model.number="row.qtySingle"
                  type="number"
                  @input="calcTotal(index)"
                  :disabled="inventoryFieldDisabled(row, 'qtySingle')"
                  :class="inventoryFieldDisabled(row, 'qtySingle') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-16 px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm"
                >
              </td>
              <td>{{ formatQuantity(row.totalQty) }}</td>
              <td class="text-right px-4">{{ money2(row.unitPrice) }}</td>
              <td class="font-black text-[#0d4169] text-right px-4 bg-slate-50/30">{{ money2(row.tPrice) }}</td>
              <td>
                <input
                  v-model="row.remark"
                  type="text"
                  :disabled="inventoryFieldDisabled(row, 'remark')"
                  :class="inventoryFieldDisabled(row, 'remark') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm"
                  placeholder="..."
                >
              </td>
            </tr>
            <tr v-if="inventory.length > 0" class="bg-slate-50 border-t-2 border-slate-200">
              <td colspan="9"></td>
              <td class="px-4 text-right text-slate-900 font-extrabold whitespace-nowrap">
                <span class="text-slate-700 font-bold mr-2">Total:</span>{{ money2(tPriceColumnSum) }}
              </td>
              <td></td>
            </tr>
            <tr v-if="inventory.length === 0 && !loading">
              <td colspan="11" class="text-center text-sm text-slate-500 py-10 italic">
                {{ selectedScopeKey && currentDate ? 'No inventory rows found for this selection.' : 'Select calendar date and department/category to view inventory.' }}
              </td>
            </tr>
            <tr v-if="loading">
              <td colspan="11" class="text-center text-sm text-slate-500 py-10 italic">Loading inventory...</td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-center mt-6">
          <button
            type="button"
            @click="openSaveModal"
            :disabled="!canSave || loading"
            :class="(!canSave || loading) ? 'opacity-60 cursor-not-allowed' : ''"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="saveModal.open"
      class="fixed inset-0 z-[10000] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
      @click.self="closeSaveModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <div class="text-left">
          <h3 class="text-base font-bold m-0">Confirm Save Inventory</h3>
        </div>
        <button
          type="button"
          class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
          @click="closeSaveModal"
          aria-label="Close save modal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
          >
        </div>
        <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSave"
          :disabled="saving"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const selectedScopeKey = ref('')
const currentDate = ref('')
const todayDate = ref(new Date().toISOString().split('T')[0])
const departments = ref([])
const incomeCategories = ref([])
const inventory = ref([])
const loadingCategories = ref(false)
const loading = ref(false)
const isSnapshotLocked = ref(false)
const saving = ref(false)

const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

const tPriceColumnSum = computed(() =>
  inventory.value.reduce((sum, row) => sum + Number(row?.tPrice || 0), 0)
)

function hasMeaningfulRowData(row) {
  const qtyPack = Number(row?.qtyPack || 0)
  const qtySingle = Number(row?.qtySingle || 0)
  const volume = Number(row?.volume || 0)
  const remark = String(row?.remark ?? '').trim()
  return qtyPack !== 0 || qtySingle !== 0 || volume !== 0 || remark.length > 0
}

const canSave = computed(() => {
  if (saving.value) return false
  if (!selectedScopeKey.value || !currentDate.value) return false
  if (isFutureDateSelected()) return false
  if (inventory.value.length === 0) return false
  if (!inventory.value.some(hasMeaningfulRowData)) return false
  if (isSnapshotLocked.value) return false
  if (!canSaveFinanceInventory.value) return false
  return true
})

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddFinanceInventory = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Finance Inventory'), 'Add')
})

const canEditFinanceInventory = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Finance Inventory'), 'Edit')
})

const canSaveFinanceInventory = computed(() => {
  if (isSnapshotLocked.value) return canEditFinanceInventory.value
  return canAddFinanceInventory.value
})

const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

function parseIssuingToSelection(key) {
  const raw = String(key || '').trim()
  if (!raw) return { departmentId: null, incomeCategoryId: null }
  if (raw.startsWith('department-')) {
    const depId = Number(raw.slice('department-'.length))
    return {
      departmentId: Number.isFinite(depId) ? depId : null,
      incomeCategoryId: null
    }
  }
  const catId = Number(raw)
  return {
    departmentId: null,
    incomeCategoryId: Number.isFinite(catId) ? catId : null
  }
}

function incomeCategoryIsActive(cat) {
  if (!cat || typeof cat !== 'object') return false
  if (cat.enabled != null) return cat.enabled === true || cat.enabled === 1 || cat.enabled === '1'
  if (cat.status != null) return Number(cat.status) === 1
  return true
}

const departmentCategoryOptions = computed(() => {
  const deptOpts = departments.value.map((dep) => ({
    key: `department-${String(dep.id)}`,
    name: String(dep.name || '').trim()
  })).filter((opt) => opt.name)

  const catOpts = incomeCategories.value.map((cat) => ({
    key: String(cat.id),
    name: String(cat.name || '').trim()
  })).filter((opt) => opt.name)

  return [...catOpts, ...deptOpts].sort((a, b) => a.name.localeCompare(b.name))
})

function buildScopeQueryParams() {
  const { departmentId, incomeCategoryId } = parseIssuingToSelection(selectedScopeKey.value)
  if (departmentId) return `departmentId=${encodeURIComponent(departmentId)}`
  if (incomeCategoryId) return `incomeCategoryId=${encodeURIComponent(incomeCategoryId)}`
  return ''
}

function buildScopeSavePayload() {
  const { departmentId, incomeCategoryId } = parseIssuingToSelection(selectedScopeKey.value)
  if (departmentId) return { departmentId }
  if (incomeCategoryId) return { incomeCategoryId }
  return {}
}

function sortByIngredientName(rows) {
  const arr = Array.isArray(rows) ? rows : []
  return arr.sort((a, b) =>
    String(a?.ingredientName || a?.name || '').localeCompare(String(b?.ingredientName || b?.name || ''), undefined, { sensitivity: 'base' })
  )
}

function normalizeUomText(uom) {
  return String(uom || '').toLowerCase().replace(/\./g, '').trim()
}

function isLiterOrKgStoreUom(row) {
  const st = normalizeUomText(row?.uomStore || row?.uomST || row?.uom_store || row?.uom || '')
  return st === 'liter' || st === 'kg' || st === 'kilogram'
}

function shouldLockVolumeAt1000(row) {
  return !isSnapshotLocked.value && isLiterOrKgStoreUom(row)
}

function enforceVolumeRuleForRow(row) {
  if (!row) return row
  if (shouldLockVolumeAt1000(row)) {
    row.volume = 1000
  } else if (!Number.isFinite(Number(row?.volume))) {
    row.volume = 0
  }
  return row
}

function isFutureDateSelected() {
  if (!currentDate.value) return false
  return currentDate.value > todayDate.value
}

function isMetric(uom) {
  if (!uom) return false
  const normalized = uom.toLowerCase().replace(/\./g, '').trim()
  return ['kg', 'liter', 'gram', 'ml', 'g', 'gm'].includes(normalized)
}

async function loadIssuingToOptions() {
  if (!companyId.value && !branchId.value) return
  loadingCategories.value = true
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')
    const depUrl = query ? `/departments?${query}` : '/departments'
    const [depRes, incomeCatRes] = await Promise.all([
      apiFetch(depUrl),
      apiFetch('/finance/income-category')
    ])

    if (depRes.ok) {
      const depData = await depRes.json().catch(() => [])
      departments.value = (Array.isArray(depData) ? depData : []).filter((d) => (d.status ?? 1) === 1)
      departments.value.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    } else {
      departments.value = []
    }

    if (incomeCatRes.ok) {
      const incomeCatData = await incomeCatRes.json().catch(() => [])
      incomeCategories.value = Array.isArray(incomeCatData)
        ? incomeCatData.filter(incomeCategoryIsActive)
        : []
      incomeCategories.value.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    } else {
      incomeCategories.value = []
    }
  } catch {
    departments.value = []
    incomeCategories.value = []
  } finally {
    loadingCategories.value = false
  }
}

function mapIngredientRows(data) {
  return data.map((row) => {
    applySinglePriceFromRecent(row)
    enforceVolumeRuleForRow(row)
    if (!isMetric(row.uomFB)) {
      row.qtySingle = 0
    }
    row.converted = Number(row?.converted ?? 0)
    row.totalQty = Number(row?.totalQty ?? 0)
    row.unitPrice = Number(row?.unitPrice ?? 0)
    row.tPrice = Number(row?.tPrice ?? 0)
    return row
  })
}

async function fetchMaterialSelectionItems() {
  const scopeQuery = buildScopeQueryParams()
  if (!scopeQuery) return null
  const res = await apiFetch(`/finance/inventory/material-selection-items?${scopeQuery}`)
  if (!res.ok) return null
  return res.json().catch(() => [])
}

async function loadInventory() {
  if (!selectedScopeKey.value || !currentDate.value) {
    inventory.value = []
    return
  }

  loading.value = true
  try {
    const scopeQuery = buildScopeQueryParams()
    if (!scopeQuery) {
      inventory.value = []
      return
    }

    if (isFutureDateSelected()) {
      isSnapshotLocked.value = true
      const data = await fetchMaterialSelectionItems()
      if (Array.isArray(data)) {
        inventory.value = data.map((row) => {
          applySinglePriceFromRecent(row)
          enforceVolumeRuleForRow(row)
          row.qtyPack = 0
          row.qtySingle = 0
          row.tPrice = 0
          row.remark = ''
          return row
        })
        sortByIngredientName(inventory.value)
      } else {
        inventory.value = []
      }
      return
    }

    const savedRes = await apiFetch(`/finance/inventory?${scopeQuery}&calendar=${currentDate.value}`)
    if (savedRes.ok) {
      const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [] }))
      let savedRows = []
      if (Array.isArray(savedPayload)) {
        savedRows = savedPayload
      } else if (savedPayload && savedPayload.rows) {
        savedRows = savedPayload.rows
      }

      if (Array.isArray(savedRows) && savedRows.length > 0) {
        isSnapshotLocked.value = true
        const processedRows = savedRows.map((row) => ({
          ...row,
          financeInventoryId: row.id,
          id: row.type === 'item' ? row.itemId : row.productId,
          name: row.ingredientName,
          converted: Number(row?.converted ?? 0),
          totalQty: Number(row?.totalQty ?? 0),
          unitPrice: Number(row?.unitPrice ?? 0),
          tPrice: Number(row?.totalPrice ?? row?.tPrice ?? 0),
          volume: Number(row?.volume ?? 0),
        }))
        inventory.value = sortByIngredientName(processedRows)
        return
      }
    }

    isSnapshotLocked.value = false
    const data = await fetchMaterialSelectionItems()
    if (!Array.isArray(data)) {
      inventory.value = []
      return
    }

    inventory.value = mapIngredientRows(data)
    inventory.value.forEach((_, i) => calcTotal(i))
    sortByIngredientName(inventory.value)
  } catch (err) {
    console.error('Error loading finance inventory:', err)
    inventory.value = []
  } finally {
    loading.value = false
  }
}

function applySinglePriceFromRecent(row) {
  if (!row) return row

  const displayedUom = normalizeUomText(getRowUomFBDisplay(row))
  const isTargetUom = ['ml', 'milliliter', 'gram', 'g', 'gm', 'bottle', 'bottles', 'btl', 'pieces', 'piece', 'pc', 'pcs', 'glass', 'glasses'].includes(displayedUom)
  if (!isTargetUom) return row

  const recentUnitPrice = Number(row.recent_unit_price ?? row.recentUnitPrice ?? 0)
  const recentVolume = Number(row.recent_volume ?? row.recentVolume ?? 0)
  if (!(Number.isFinite(recentUnitPrice) && Number.isFinite(recentVolume) && recentVolume > 0)) {
    return row
  }

  let nextSingle = recentUnitPrice / recentVolume
  if (displayedUom === 'glass' || displayedUom === 'glasses') {
    const glassAmount = Number(row.glass_amount ?? row.glassAmount ?? 0)
    if (!(Number.isFinite(glassAmount) && glassAmount > 0)) return row
    nextSingle = nextSingle * glassAmount
  }

  row.uPriceSingle = nextSingle
  return row
}

function inventoryFieldDisabled(row, field) {
  if (isFutureDateSelected()) return true

  if (field === 'qtyPack' || field === 'volume') {
    const st = normalizeUomText(row?.uomStore || row?.uomST || row?.uom_store || row?.uom || '')
    const fb = normalizeUomText(row?.uomFB || row?.uomPro || row?.uom_fb || '')
    if (st && fb && st === fb) return true
  }
  if (field === 'volume' && shouldLockVolumeAt1000(row)) return true

  if (isSnapshotLocked.value) return true

  return false
}

function getRowUomFBDisplay(row) {
  return row?.uomFB
}

function getRowUPriceSingle(row, uomFBDisplay = null) {
  if (uomFBDisplay === 'Gram' || uomFBDisplay === 'M.L.') {
    return Number(row?.uPriceSingle || 0) * 1000
  }
  return Number(row?.uPriceSingle || 0)
}

function getRowVolume(row) {
  const vol = Number(row?.volume ?? 0)
  return Number.isFinite(vol) && vol > 0 ? vol : 0
}

function getRowConverted(row) {
  return Number(row?.qtyPack || 0) * getRowVolume(row)
}

function getRowTotalQty(row, uomFBDisplay = null) {
  if (uomFBDisplay === 'Gram' || uomFBDisplay === 'M.L.') {
    return (getRowConverted(row) + Number(row?.qtySingle || 0)) / 1000
  }
  return getRowConverted(row) + Number(row?.qtySingle || 0)
}

function calcTotal(index) {
  const row = inventory.value[index]
  if (!row) return
  enforceVolumeRuleForRow(row)
  const uomFBDisplay = getRowUomFBDisplay(row)
  row.converted = Number(getRowConverted(row) || 0)
  row.totalQty = Number(getRowTotalQty(row, uomFBDisplay) || 0)
  row.unitPrice = Number(getRowUPriceSingle(row, uomFBDisplay) || 0)
  row.tPrice = Number(row.totalQty || 0) * Number(row.unitPrice || 0)
}

function formatQuantity(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 5 })
}

function money2(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (saving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

async function saveAllRows() {
  if (!canSave.value) return false
  saving.value = true
  try {
    const itemsToSave = (Array.isArray(inventory.value) ? inventory.value : []).map((row) => ({
      ...row,
      qtyPack: Number(row?.qtyPack || 0),
      volume: Number(row?.volume ?? 0),
      converted: Number(row?.converted ?? 0),
      qtySingle: Number(row?.qtySingle || 0),
      totalQty: Number(row?.totalQty ?? 0),
      uPriceSingle: Number(row?.unitPrice ?? row?.uPriceSingle ?? 0),
      unitPrice: Number(row?.unitPrice ?? 0),
      tPrice: Number(row?.tPrice ?? 0),
    }))
    if (itemsToSave.length === 0) {
      alert('No inventory rows to save.')
      return false
    }

    const res = await apiFetch('/finance/inventory/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        ...buildScopeSavePayload(),
        items: itemsToSave,
      }),
    })
    if (res.ok) {
      alert('All rows saved successfully!')
      selectedScopeKey.value = ''
      inventory.value = []
      return true
    }
    const err = await res.json().catch(() => ({}))
    alert('Error saving: ' + (err.message || 'Unknown error'))
    return false
  } catch (err) {
    console.error('Error saving rows:', err)
    alert('Failed to save rows')
    return false
  } finally {
    saving.value = false
  }
}

watch(departmentCategoryOptions, (options) => {
  if (options.length === 1 && !selectedScopeKey.value) {
    selectedScopeKey.value = options[0].key
    setTimeout(() => { loadInventory() }, 50)
  }
})

onMounted(async () => {
  currentDate.value = ''
  await loadIssuingToOptions()
})
</script>

<style scoped>
.daily-inventory-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
  border-right: 2px solid #e2e8f0 !important;
}

.no-side-border {
  border-right: none !important;
  border-left: none !important;
}

th.sticky-col {
  z-index: 30;
}

.custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
</style>
